/**
 * Cable Path Service Adapter
 *
 * Adapter for the standardized entity hook pattern.
 * Wraps cablePathService for use with useEntity hook.
 */

import {
  getCablePaths,
  createCablePath,
  updateCablePath,
  deleteCablePath,
} from '@/services/cablePathService';
import type { CablePath, UpdateCablePath } from '@/types/cablePaths';

export const cablePathServiceAdapter = {
  async getByFloorPlan(floorPlanId: string): Promise<CablePath[]> {
    return getCablePaths(floorPlanId);
  },

  async create(item: Partial<CablePath>): Promise<CablePath> {
    // Ensure required fields
    if (!item.floor_plan_id) {
      throw new Error('floor_plan_id is required');
    }

    return createCablePath({
      floor_plan_id: item.floor_plan_id,
      name: item.name ?? null,
      start_junction_id: item.start_junction_id ?? null,
      end_junction_id: item.end_junction_id ?? null,
      points: item.points ?? [],
      length_real: item.length_real ?? null,
      length_unit: item.length_unit ?? 'ft',
      path_type: item.path_type ?? 'conduit',
      route: item.route ?? 'ceiling',
      conduit: item.conduit ?? null,
      cables: item.cables ?? [],
      vertical_drop_start: item.vertical_drop_start ?? 10,
      vertical_drop_end: item.vertical_drop_end ?? 10,
      vertical_drop_unit: item.vertical_drop_unit ?? 'ft',
      created_by_clerk_id: item.created_by_clerk_id ?? null,
    });
  },

  async update(id: string, updates: Partial<CablePath>): Promise<CablePath> {
    // Filter out non-updatable fields
    const { id: _, floor_plan_id: __, created_at: ___, ...updateData } = updates;
    return updateCablePath(id, updateData as UpdateCablePath);
  },

  async delete(id: string): Promise<boolean> {
    return deleteCablePath(id);
  },
};
